import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Channel } from '@/lib/channels';
import { ChannelCard } from './ChannelCard';
import { EmptyState } from '@/components/common/EmptyState';
import { ChannelGridSkeleton } from '@/components/common/Skeleton';
import { Tv } from 'lucide-react';

interface ChannelGridProps {
  channels: Channel[];
  onChannelClick: (channel: Channel) => void;
  activeChannelId?: string;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  columns?: number;
}

export function ChannelGrid({
  channels,
  onChannelClick,
  activeChannelId,
  isLoading,
  emptyTitle = 'No channels found',
  emptyDescription = 'Try adjusting your filters or search.',
  emptyAction,
  columns = 4,
}: ChannelGridProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Group channels into rows of `columns` for virtualization.
  // Hooks must run unconditionally — compute rowCount even if we'll
  // early-return on the next lines.
  const rowCount = Math.max(1, Math.ceil(channels.length / columns));

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 280,
    overscan: 3,
    // Measure each row's actual height so cards never overlap when the
    // estimate is wrong (e.g. narrow right panel, long channel names,
    // broadcaster badge present). The row div must use `ref` and
    // `data-index` for this to work.
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  if (isLoading) {
    return <ChannelGridSkeleton count={12} />;
  }

  if (channels.length === 0) {
    return (
      <EmptyState
        icon={<Tv className="w-6 h-6" />}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  // Container-query breakpoints. The parent of this grid must have
  // the `@container` class — otherwise the `@md:` / `@lg:` variants
  // fall back to viewport-based matching (same as `md:` / `lg:`).
  const colClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 @md:grid-cols-3',
    4: 'grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4',
    5: 'grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5',
  }[columns] || 'grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4';

  return (
    <div ref={parentRef} className="h-full overflow-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const startIdx = virtualRow.index * columns;
          const rowChannels = channels.slice(startIdx, startIdx + columns);
          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className={`absolute top-0 left-0 w-full grid gap-4 ${colClass} px-1 pb-4`}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {rowChannels.map((channel) => (
                <ChannelCard
                  key={channel.id}
                  channel={channel}
                  onClick={onChannelClick}
                  isActive={channel.id === activeChannelId}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}